import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";
import CommandPalette from "./CommandPalette";
import MaXSidebarLogo from "./MaXSidebarLogo";
import { usePermissions } from "../hooks/usePermissions";
import { useNavigationAccess } from "../navigation/useNavigationAccess";
import {
  ADMIN_MENU_ITEMS_RAW,
  flattenAdminNavForPalette,
} from "../navigation/adminNavConfig";
import { authService } from "../services";
import "./AdminDashboard.css";

/** Longest matching path wins (e.g. /feature-apps vs /features). */
function getActiveAdminPath(pathname, items) {
  let best = null;
  for (const item of items) {
    const p = item.path;
    if (pathname === p || pathname.startsWith(`${p}/`)) {
      if (!best || p.length > best.length) best = p;
    }
  }
  return best;
}

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = authService.getUser();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { ctx, featuresLoading, filterItems } = useNavigationAccess();

  const adminNavItems = useMemo(
    () => filterItems(ADMIN_MENU_ITEMS_RAW),
    [filterItems],
  );

  const activeAdminPath = useMemo(
    () => getActiveAdminPath(location.pathname, adminNavItems),
    [location.pathname, adminNavItems],
  );

  const getCurrentPageTitle = useCallback(() => {
    const item = adminNavItems.find((i) => i.path === activeAdminPath);
    return item?.label || "Admin";
  }, [adminNavItems, activeAdminPath]);

  useEffect(() => {
    document.title = `${getCurrentPageTitle()} · MaX Admin`;
  }, [getCurrentPageTitle]);

  const { hasPermission } = usePermissions();
  const canManageAdmin = hasPermission("Admin.Manage");

  const paletteEntries = useMemo(() => {
    if (featuresLoading) return [];
    return flattenAdminNavForPalette(ctx);
  }, [ctx, featuresLoading]);

  if (!canManageAdmin) {
    return (
      <div className="admin-dashboard-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the Admin Dashboard.</p>
          <button onClick={() => navigate("/home")} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <main className="admin-dashboard-main">
        <header className="admin-shell-header">
          <div className="admin-shell-top">
            <Link
              to="/admin/dashboard/tenants"
              className="admin-shell-brand"
              title="Admin home"
            >
              <span className="admin-shell-logo-wrap">
                <MaXSidebarLogo isOpen to="/admin/dashboard/tenants" title="MaX Admin" />
              </span>
            </Link>
            <div className="admin-shell-top-center">
              <button
                type="button"
                className="admin-shell-search-trigger"
                onClick={() => setPaletteOpen(true)}
                aria-label="Open command palette"
              >
                <HiOutlineMagnifyingGlass aria-hidden />
                <span className="admin-shell-search-label">Search admin</span>
                <kbd className="admin-shell-kbd">⌘K</kbd>
              </button>
            </div>
            <div className="admin-shell-trailing">
              <button
                type="button"
                className="admin-shell-back-dashboard"
                onClick={() => navigate("/home")}
              >
                Back to Dashboard
              </button>
              <button
                type="button"
                className="admin-shell-user-chip"
                onClick={() => navigate("/home/profile")}
                title="Profile"
              >
                <span className="admin-shell-user-avatar">
                  {user?.fullName?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </button>
            </div>
          </div>

          <nav className="admin-top-nav" aria-label="Admin sections">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const active = item.path === activeAdminPath;
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`admin-top-nav-item ${active ? "active" : ""}`}
                >
                  <Icon className="admin-top-nav-icon" aria-hidden />
                  <span className="admin-top-nav-label">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </header>

        <div className="admin-dashboard-content">
          <Outlet />
        </div>
      </main>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        entries={paletteEntries}
        loading={featuresLoading}
      />
    </div>
  );
};

export default AdminDashboard;
